import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { Badge } from '../components/badges';
import { Topbar } from '../components/Shell';
import { Empty, ErrorBox, Field, Loading, Modal, Seg, useAction, useToast } from '../components/ui';
import { ApiError, get, post, qs } from '../lib/api';
import { fmtAgo, fmtDateTime, fromGmtInput, STATUS_LABEL, toGmtInput } from '../lib/format';
import type { FeedFixtureList, FeedFixtureRow, FeedTeamRow, GameSettings, ImportResult, Round, Team } from '../lib/types';

/**
 * The football data API, from the console's side. Everything here is picked
 * through opaque refs: the feed's own ids never reach the browser.
 */

interface FeedStatus {
  configured: boolean;
  timezone: string | null;
  today: string | null;
  /** Provider requests in the last hour, against FEED_MAX_REQUESTS_PER_HOUR (0 = no limit). */
  usage: { lastHour: number; limit: number } | null;
  /** How fresh the match data is (see feedFreshness in core). */
  freshness: { stuckInPlay: number; notCheckedForAnHour: number; lastChecked: string | null } | null;
}

/** Warns when the feed has stopped keeping matches up to date. */
export function FeedFreshness({ f }: { f: FeedStatus['freshness'] }) {
  if (!f) return null;
  if (!f.stuckInPlay && !f.notCheckedForAnHour) {
    return <span className="muted small">{f.lastChecked ? `Match data checked ${fmtAgo(f.lastChecked)}` : 'No feed match checked yet'}</span>;
  }
  return (
    <div className="err-box">
      {f.stuckInPlay > 0 && <>{f.stuckInPlay} match(es) still "in play" more than 4 hours after kickoff. </>}
      {f.notCheckedForAnHour > 0 && <>{f.notCheckedForAnHour} started match(es) not confirmed and not checked by the feed for over an hour. </>}
      The catch-up sync retries every 15 minutes; if this persists, check the feed and the API logs, or set the result by hand on the match screen.
      {f.lastChecked && <> Last successful check {fmtAgo(f.lastChecked)}.</>}
    </div>
  );
}

/** "123 / 400 feed calls this hour", amber past 70% (match pages stop there), red past 85%. */
export function FeedUsage({ usage }: { usage: FeedStatus['usage'] }) {
  if (!usage) return null;
  const share = usage.limit ? usage.lastHour / usage.limit : 0;
  const cls = share >= 0.85 ? 'pill pill-red' : share >= 0.7 ? 'pill pill-amber' : 'pill';
  return (
    <span className={cls} title="Requests to the football data provider in the last hour. As the budget fills, match-page extras stop first (70%), then schedule checks (85%); live scores keep going.">
      {usage.lastHour}
      {usage.limit ? ` / ${usage.limit}` : ''} feed calls this hour
    </span>
  );
}

export function useFeedStatus() {
  return useQuery({ queryKey: ['feed', 'status'], queryFn: () => get<FeedStatus>('/admin/feed/status'), staleTime: 60_000 });
}

function FeedOff() {
  return (
    <div className="note-box">
      The football data API isn't configured. Set <code>FOOTBALL_API_BASE_URL</code> in the API's <code>.env</code> and restart it.
    </div>
  );
}

function DateField({ value, onChange, timezone }: { value: string; onChange: (v: string) => void; timezone: string | null }) {
  const shift = (days: number) => {
    const d = new Date(`${value}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    onChange(d.toISOString().slice(0, 10));
  };
  return (
    <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
      <button type="button" className="btn btn-sm" onClick={() => shift(-1)} aria-label="Previous day">
        ‹
      </button>
      <input className="input" type="date" aria-label="Day" value={value} onChange={(e) => e.target.value && onChange(e.target.value)} style={{ width: 160 }} />
      <button type="button" className="btn btn-sm" onClick={() => shift(1)} aria-label="Next day">
        ›
      </button>
      {timezone && <span className="muted small">Days in {timezone}</span>}
    </div>
  );
}

const fixtureStatus = (f: FeedFixtureRow) =>
  f.clock ? f.clock : f.status === 'SCHEDULED' ? new Date(f.kickoffAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : STATUS_LABEL[f.status];

// ================================================================ import page

/** A fixture the admin ticked, kept while they move between days. */
type Picked = FeedFixtureRow & { competition: string };

/**
 * Same fixture, whichever day list it came from. (Refs can't be used as keys:
 * they're re-sealed on every fetch.)
 */
const keyOf = (f: Pick<FeedFixtureRow, 'kickoffAt' | 'home' | 'away'>) => `${f.kickoffAt}|${f.home.name}|${f.away.name}`;

export function FeedImportPage() {
  const status = useFeedStatus();
  const search = useSearch({ strict: false }) as { date?: string };
  const navigate = useNavigate();
  const date = search.date ?? status.data?.today ?? new Date().toISOString().slice(0, 10);
  const setDate = (d: string) => navigate({ to: '/matches/import', search: { date: d } });
  const list = useQuery({
    queryKey: ['feed', 'fixtures', date],
    queryFn: () => get<FeedFixtureList>(`/admin/feed/fixtures${qs({ date })}`),
    enabled: !!status.data?.configured,
  });
  const [picked, setPicked] = useState<Map<string, Picked>>(new Map());
  const [results, setResults] = useState<ImportResult[] | null>(null);
  const [following, setFollowing] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const basket = useMemo(() => [...picked.values()].sort((a, b) => a.kickoffAt.localeCompare(b.kickoffAt)), [picked]);
  const toggle = (f: FeedFixtureRow, competition: string, on?: boolean) =>
    setPicked((m) => {
      const n = new Map(m);
      const k = keyOf(f);
      if (on ?? !n.has(k)) n.set(k, { ...f, competition });
      else n.delete(k);
      return n;
    });

  if (status.isLoading) return <Loading />;
  return (
    <>
      <Topbar
        title="Import from the data feed"
        sub="A day's fixtures from the football data API, in the leagues the game follows. Pick from as many days as you like."
        actions={
          <>
            <Link to="/matches" className="btn">
              All matches
            </Link>
            {status.data?.configured && (
              <button type="button" className="btn" onClick={() => setFollowing(true)}>
                Follow a league
              </button>
            )}
          </>
        }
      >
        {status.data?.configured && (
          <>
            <DateField value={date} onChange={setDate} timezone={status.data.timezone} />
            <FeedUsage usage={status.data.usage} />
          </>
        )}
      </Topbar>
      <div className="page stack" style={{ gap: 20 }}>
        {!status.data?.configured ? (
          <FeedOff />
        ) : (
          <>
            <FeedFreshness f={status.data.freshness} />
            {results && (
              <section className="card" aria-labelledby="res-h">
                <div className="card-head">
                  <h2 id="res-h">Last import</h2>
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => setResults(null)}>
                    Hide
                  </button>
                </div>
                <table className="table">
                  <tbody>
                    {results.map((r, i) => (
                      <tr key={i}>
                        <td>
                          {r.home} v {r.away}
                        </td>
                        <td>{r.ok ? <span className="pill pill-green">Imported</span> : <span className="pill pill-amber">{r.reason}</span>}</td>
                        <td className="muted small">{r.createdTeams.length ? `New teams: ${r.createdTeams.join(', ')}` : ''}</td>
                        <td className="right">
                          {r.matchId && (
                            <Link to="/matches/$matchId" params={{ matchId: String(r.matchId) }} className="btn btn-sm">
                              Open
                            </Link>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            )}

            <div className="cols cols-main">
              <div className="stack" style={{ gap: 20 }}>
                {list.error && <ErrorBox error={list.error} />}
                {list.isLoading && <Loading what="Asking the data feed" />}
                {list.data && list.data.leagues.length === 0 && (
                  <Empty>
                    None of the followed leagues play on this day.{' '}
                    {list.data.otherLeagues.length > 0 && (
                      <button type="button" className="btn btn-sm" onClick={() => setFollowing(true)}>
                        Follow another league
                      </button>
                    )}
                  </Empty>
                )}
                {list.data?.leagues.map((l) => {
                  const open = l.fixtures.filter((f) => f.importable);
                  const allPicked = open.length > 0 && open.every((f) => picked.has(keyOf(f)));
                  return (
                    <section key={l.competition.id} className="card" aria-label={l.competition.name}>
                      <div className="card-head">
                        <h2 className="row" style={{ gap: 8 }}>
                          <Badge spec={l.competition.badge} size={22} /> {l.competition.name}
                        </h2>
                        {open.length > 0 && (
                          <button type="button" className="btn btn-sm" onClick={() => open.forEach((f) => toggle(f, l.competition.name, !allPicked))}>
                            {allPicked ? 'Clear' : `Pick all ${open.length}`}
                          </button>
                        )}
                      </div>
                      <table className="table">
                        <tbody>
                          {l.fixtures.map((f) => (
                            <tr key={keyOf(f)} className={f.importable ? 'clickable' : 'dim'} onClick={() => f.importable && toggle(f, l.competition.name)}>
                              <td style={{ width: 32 }}>
                                <input
                                  type="checkbox"
                                  aria-label={`Import ${f.home.name} v ${f.away.name}`}
                                  disabled={!f.importable}
                                  checked={picked.has(keyOf(f))}
                                  onChange={() => toggle(f, l.competition.name)}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </td>
                              <td className="num" style={{ width: 70 }}>
                                {fixtureStatus(f)}
                              </td>
                              <td>
                                <FeedSide side={f.home} align="end" />
                              </td>
                              <td className="num" style={{ width: 54, textAlign: 'center' }}>
                                {f.score ? `${f.score[0]} – ${f.score[1]}` : 'v'}
                              </td>
                              <td>
                                <FeedSide side={f.away} />
                              </td>
                              <td className="muted small" style={{ whiteSpace: 'nowrap' }}>
                                {f.round ? `Round ${f.round}` : ''}
                              </td>
                              <td className="right">
                                {f.matchId ? (
                                  <Link to="/matches/$matchId" params={{ matchId: String(f.matchId) }} className="btn btn-sm" onClick={(e) => e.stopPropagation()}>
                                    Imported · open
                                  </Link>
                                ) : !f.importable ? (
                                  <span className="pill">{f.status === 'SCHEDULED' ? 'Kicked off' : STATUS_LABEL[f.status]}</span>
                                ) : null}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </section>
                  );
                })}
              </div>

              <aside className="stack" style={{ gap: 20 }}>
                <section className="card" aria-labelledby="imp-h" style={{ position: 'sticky', top: 16 }}>
                  <div className="card-head">
                    <h2 id="imp-h">Picked fixtures</h2>
                    {basket.length > 0 && (
                      <button type="button" className="btn btn-sm btn-ghost" onClick={() => setPicked(new Map())}>
                        Clear all
                      </button>
                    )}
                  </div>
                  {basket.length === 0 ? (
                    <Empty>Tick fixtures on any day; they stay here while you change days.</Empty>
                  ) : (
                    <div style={{ maxHeight: 420, overflow: 'auto' }}>
                      <table className="table">
                        <tbody>
                          {basket.map((f) => (
                            <tr key={keyOf(f)}>
                              <td className="small num" style={{ whiteSpace: 'nowrap' }}>
                                {fmtDateTime(f.kickoffAt)}
                              </td>
                              <td className="small">
                                {f.home.name} v {f.away.name}
                                <div className="muted">{f.competition}</div>
                              </td>
                              <td className="right">
                                <button type="button" className="btn btn-sm btn-ghost" aria-label={`Remove ${f.home.name} v ${f.away.name}`} onClick={() => toggle(f, f.competition, false)}>
                                  ✕
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  <div className="card-foot row">
                    <span className="grow muted small">
                      {basket.length ? `${basket.length} fixture(s) over ${new Set(basket.map((f) => dayKey(f.kickoffAt))).size} day(s)` : ''}
                    </span>
                    <button type="button" className="btn btn-primary" disabled={basket.length === 0} onClick={() => setConfirming(true)}>
                      Import {basket.length || ''}…
                    </button>
                  </div>
                </section>
                <span className="muted small">
                  Teams the game doesn't have yet are created from the data feed: name, country, a badge in the club's colours (never the real crest) and the squad
                  for first-scorer picks. Scores, goals and status then follow the feed live.
                </span>
              </aside>
            </div>
          </>
        )}
      </div>
      {following && list.data && <FollowLeagueModal date={date} onClose={() => setFollowing(false)} />}
      {confirming && (
        <ImportModal
          fixtures={basket}
          onClose={() => setConfirming(false)}
          onDone={(r) => {
            setResults(r);
            // Keep only what didn't go through, so it can be retried.
            setPicked((m) => {
              const n = new Map(m);
              basket.forEach((f, i) => r[i]?.ok && n.delete(keyOf(f)));
              return n;
            });
            setConfirming(false);
          }}
        />
      )}
    </>
  );
}

/** Calendar day in GMT (rounds are whole GMT days). */
const dayKey = (iso: string) => toGmtInput(iso).slice(0, 10);

/**
 * Asks which round the picked fixtures go in. A new round runs from the start
 * of the first match's day to the end of the last match's day (editable).
 */
function ImportModal({ fixtures, onClose, onDone }: { fixtures: Picked[]; onClose: () => void; onDone: (r: ImportResult[]) => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => get<{ settings: GameSettings }>('/admin/settings') });
  const season = settings.data?.settings.currentSeason;
  const rounds = useQuery({ queryKey: ['rounds', season], queryFn: () => get<{ rounds: Round[] }>(`/admin/rounds${qs({ season })}`), enabled: !!season });
  const first = fixtures[0]!.kickoffAt;
  const last = fixtures[fixtures.length - 1]!.kickoffAt;
  const [mode, setMode] = useState<'new' | 'existing'>('new');
  const [name, setName] = useState('');
  const [startsAt, setStartsAt] = useState(`${dayKey(first)}T00:00`);
  const [endsAt, setEndsAt] = useState(`${dayKey(last)}T23:59`);
  const [roundId, setRoundId] = useState<number | undefined>();
  const [published, setPublished] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { busy, run } = useAction();
  const sameName = rounds.data?.rounds.find((r) => r.name.trim().toLowerCase() === name.trim().toLowerCase());
  const startIso = fromGmtInput(startsAt);
  // The end is inclusive of its last minute: 23:59 means the whole day.
  const endIso = fromGmtInput(endsAt) ? new Date(new Date(fromGmtInput(endsAt)!).getTime() + 59_999).toISOString() : null;
  const outside = mode === 'new' && !sameName && startIso && endIso ? fixtures.filter((f) => f.kickoffAt < startIso || f.kickoffAt > endIso) : [];

  const submit = () => {
    setError(null);
    if (mode === 'new' && !name.trim()) return setError('Give the round a name');
    if (mode === 'new' && !sameName && (!startIso || !endIso || endIso <= startIso)) return setError('The round must end after it starts');
    if (mode === 'existing' && !roundId) return setError('Pick a round');
    void run(async () => {
      const r = await post<{ results: ImportResult[] }>('/admin/feed/fixtures/import', {
        refs: fixtures.map((f) => f.ref),
        published,
        round: mode === 'existing' ? { id: roundId } : sameName ? { id: sameName.id } : { create: { name: name.trim(), season, startsAt: startIso, endsAt: endIso } },
      });
      const ok = r.results.filter((x) => x.ok).length;
      toast(ok === r.results.length ? `${ok} match(es) imported` : `${ok} of ${r.results.length} imported`, ok < r.results.length);
      await qc.invalidateQueries();
      onDone(r.results);
    });
  };

  return (
    <Modal
      title={`Import ${fixtures.length} fixture(s)`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={busy} onClick={submit}>
            {busy ? 'Importing…' : 'Import'}
          </button>
        </>
      }
    >
      <p className="muted small" style={{ marginTop: 0 }}>
        {fmtDateTime(first)} to {fmtDateTime(last)} · {new Set(fixtures.map((f) => f.competition)).size} competition(s)
      </p>
      <Seg
        label="Round"
        value={mode}
        onChange={setMode}
        options={[
          ['new', 'New round'],
          ['existing', 'Existing round'],
        ]}
      />
      {mode === 'new' ? (
        <div className="stack">
          <Field label="Round name">
            <input className="input" value={name} maxLength={40} onChange={(e) => setName(e.target.value)} placeholder="e.g. Week 43" autoFocus />
          </Field>
          {sameName ? (
            <div className="note-box">
              {sameName.name} already exists ({fmtDateTime(sameName.startsAt)} to {fmtDateTime(sameName.endsAt)}): the matches join it and its dates stay.
            </div>
          ) : (
            <div className="form-grid">
              <Field label="Starts (GMT)" hint="00:00 GMT on the first match's day">
                <input className="input" type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
              </Field>
              <Field label="Ends (GMT)" hint="End of the last match's day, GMT. The next week opens to players at 00:00 GMT the day after">
                <input className="input" type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
              </Field>
            </div>
          )}
          {outside.length > 0 && <div className="note-box">{outside.length} fixture(s) kick off outside these dates.</div>}
        </div>
      ) : (
        <Field label="Round">
          <select className="select" value={roundId ?? ''} onChange={(e) => setRoundId(e.target.value ? Number(e.target.value) : undefined)}>
            <option value="">Pick a round</option>
            {rounds.data?.rounds.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name} · {r.publishedCount}/{r.matchCount} published
              </option>
            ))}
          </select>
        </Field>
      )}
      <label className="check">
        <input type="checkbox" checked={published} onChange={(e) => setPublished(e.target.checked)} />
        Publish now (players can predict them)
      </label>
      {error && <div className="err-box">{error}</div>}
    </Modal>
  );
}

function FeedSide({ side, align }: { side: FeedFixtureRow['home']; align?: 'end' }) {
  return (
    <span className="row" style={{ gap: 8, flexWrap: 'nowrap', justifyContent: align === 'end' ? 'flex-end' : 'flex-start' }}>
      {align === 'end' && <span>{side.name}</span>}
      {side.team ? <Badge spec={side.team.badge} size={20} /> : <span className="pill" title="Created from the data feed on import">new</span>}
      {align !== 'end' && <span>{side.name}</span>}
    </span>
  );
}

// ================================================================ follow a league

/** Leagues playing on a day that the game doesn't follow yet. */
export function FollowLeagueModal({ date: initial, onClose }: { date?: string; onClose: () => void }) {
  const status = useFeedStatus();
  const qc = useQueryClient();
  const [date, setDate] = useState(initial ?? status.data?.today ?? new Date().toISOString().slice(0, 10));
  const [q, setQ] = useState('');
  const list = useQuery({
    queryKey: ['feed', 'fixtures', date],
    queryFn: () => get<FeedFixtureList>(`/admin/feed/fixtures${qs({ date })}`),
    enabled: !!status.data?.configured,
  });
  const { busy, run } = useAction();
  const shown = (list.data?.otherLeagues ?? []).filter((l) => `${l.name} ${l.country ?? ''}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <Modal wide title="Follow a league from the data feed" onClose={onClose}>
      {!status.data?.configured ? (
        <FeedOff />
      ) : (
        <>
          <p className="muted small" style={{ marginTop: 0 }}>
            Leagues with fixtures on the day you pick. A followed league is a competition: its fixtures can be imported and they follow the feed live.
          </p>
          <div className="row" style={{ gap: 12 }}>
            <DateField value={date} onChange={setDate} timezone={null} />
            <input className="input grow" placeholder="Search leagues or countries" aria-label="Search leagues" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          {list.error && <ErrorBox error={list.error} />}
          {list.isLoading ? (
            <Loading what="Asking the data feed" />
          ) : shown.length === 0 ? (
            <Empty>No other leagues play that day.</Empty>
          ) : (
            <div className="table-wrap" style={{ maxHeight: 420, overflow: 'auto' }}>
              <table className="table">
                <tbody>
                  {shown.map((l) => (
                    <tr key={l.ref}>
                      <td>
                        <strong>{l.name}</strong>
                      </td>
                      <td className="muted">{l.country ?? ''}</td>
                      <td className="muted small">{l.fixtures} fixture(s)</td>
                      <td className="right">
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          disabled={busy}
                          onClick={() =>
                            run(async () => {
                              await post('/admin/feed/leagues', { ref: l.ref });
                              await qc.invalidateQueries({ queryKey: ['competitions'] });
                              await qc.invalidateQueries({ queryKey: ['feed', 'fixtures'] });
                            }, `Following ${l.name}`)
                          }
                        >
                          Follow
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

// ================================================================ team from the feed

/** "New team → from the data feed": teams playing on a day in followed leagues. */
export function FeedTeamPicker({ onDone }: { onDone: (team: Team) => void }) {
  const status = useFeedStatus();
  const qc = useQueryClient();
  const [date, setDate] = useState(status.data?.today ?? new Date().toISOString().slice(0, 10));
  const [q, setQ] = useState('');
  const list = useQuery({
    queryKey: ['feed', 'teams', date],
    queryFn: () => get<{ teams: FeedTeamRow[] }>(`/admin/feed/teams${qs({ date })}`),
    enabled: !!status.data?.configured,
  });
  const { busy, run } = useAction();
  if (status.isLoading) return <Loading />;
  if (!status.data?.configured) return <FeedOff />;
  const shown = (list.data?.teams ?? []).filter((t) => `${t.name} ${t.league}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div className="stack">
      <p className="muted small" style={{ margin: 0 }}>
        Teams playing on the day you pick, in the leagues the game follows. The data feed gives the name, country, club colours (for a generated badge, never
        the real crest) and the squad.
      </p>
      <div className="row" style={{ gap: 12 }}>
        <DateField value={date} onChange={setDate} timezone={null} />
        <Field label="Search">
          <input className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Team or league" />
        </Field>
      </div>
      {list.error && <ErrorBox error={list.error} />}
      {list.isLoading ? (
        <Loading what="Asking the data feed" />
      ) : shown.length === 0 ? (
        <Empty>No teams of followed leagues play that day.</Empty>
      ) : (
        <div className="table-wrap" style={{ maxHeight: 380, overflow: 'auto' }}>
          <table className="table">
            <tbody>
              {shown.map((t) => (
                <tr key={t.ref}>
                  <td style={{ width: 32 }}>{t.team && <Badge spec={t.team.badge} size={22} />}</td>
                  <td>
                    <strong>{t.name}</strong>
                  </td>
                  <td className="muted small">{t.league}</td>
                  <td className="right">
                    {t.team ? (
                      <Link to="/teams/$teamId" params={{ teamId: String(t.team.id) }} className="btn btn-sm">
                        Open
                      </Link>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        disabled={busy}
                        onClick={() =>
                          run(async () => {
                            try {
                              const r = await post<{ team: Team }>('/admin/feed/teams', { ref: t.ref });
                              await qc.invalidateQueries({ queryKey: ['teams'] });
                              await qc.invalidateQueries({ queryKey: ['feed', 'teams'] });
                              onDone(r.team);
                            } catch (e) {
                              if (e instanceof ApiError && e.code === 'BAD_REF') await list.refetch();
                              throw e;
                            }
                          }, `${t.name} created`)
                        }
                      >
                        Create
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

